import { config as companyTableConfig } from '@aibees/elephant/feature/search-table-company/config';
import { useBrowseCompanySearchCore } from '@aibees/elephant/feature/search-table-company/hooks/use-browse-company-search';
import { config as peopleTableConfig } from '@aibees/elephant/feature/search-table-people/config';
import { useBrowsePeopleSearchCore } from '@aibees/elephant/feature/search-table-people/hooks/use-browse-people-search';
import {
  useSearchModeFromRoute,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import { SearchType } from '@aibees/elephant/types';
import { AdvanceTableActionFilter } from '@aibees/shared/features';
import { toNumber } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { useDeferredValue, useMemo } from 'react';

export function useSearchApi(extraFilters?: AdvanceTableActionFilter) {
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  if (deferredParams.id && type === SearchType.People)
    extraFilters = { ...extraFilters, company_id: deferredParams.id };

  const companyDeps = useMemo(
    () => ({
      workspace,
      type,
      mode,
      searchParams: { ...deferredParams, ...extraFilters },
      modalityVersion,
    }),
    [workspace, type, mode, deferredParams, extraFilters, modalityVersion]
  );

  const peopleDeps = useMemo(
    () => ({
      workspace,
      type,
      searchParams: { ...deferredParams, ...extraFilters },
      modalityVersion,
    }),
    [workspace, type, deferredParams, extraFilters, modalityVersion]
  );

  const companySearchApi = useBrowseCompanySearchCore(
    {
      page: toNumber(deferredParams?.page) ?? 0,
      size: toNumber(deferredParams?.size) ?? 25,
      sort:
        companyTableConfig.prependSortingFields &&
        Object.keys(companyTableConfig.prependSortingFields)
          .map((x) => `${x}:${companyTableConfig.prependSortingFields?.[x]}`)
          .join(','),
    },
    companyDeps,
    {
      enabled: type === SearchType.Company,
    },
    true
  );
  const peopleSearchApi = useBrowsePeopleSearchCore(
    {
      page: toNumber(deferredParams?.page) ?? 0,
      size: toNumber(deferredParams?.size) ?? 25,
      sort:
        peopleTableConfig.prependSortingFields &&
        Object.keys(peopleTableConfig.prependSortingFields)
          .map((x) => `${x}:${peopleTableConfig.prependSortingFields?.[x]}`)
          .join(','),
    },
    peopleDeps,
    { enabled: type === SearchType.People },
    true
  );

  return useMemo(() => {
    if (type === SearchType.Company) {
      return {
        api: companySearchApi,
      };
    }
    return {
      api: peopleSearchApi,
    };
  }, [type, companySearchApi, peopleSearchApi]);
}
